import React, { useEffect, useState } from 'react';
import { History, ArrowUpRight, ArrowDownRight, Loader2 } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { money } from '@/data/constants';
import {
  LedgerEntry,
  PaymentEntry,
  fetchLedger,
  fetchPayments,
  ledgerLabel,
  onCreditsChanged,
} from '@/lib/creditsBus';

/**
 * CREDIT TRANSACTION HISTORY + PAYMENT HISTORY.
 *
 * Every row is written server-side in the same transaction as the credit
 * movement it describes, so this is an audit trail rather than a reconstruction:
 * credits added (packs, $29 singles, membership grants and renewals), credits
 * spent (lead unlocks) and credits forfeited at the end of a cycle.
 *
 * Reads are RLS-scoped — a user can only ever see their own rows.
 */
interface Props {
  userId?: string;
  /** Current spendable balance, passed in so the card cannot disagree with the pill. */
  balance: number;
  showPayments?: boolean;
}

const dateLabel = (iso: string, lang: string) => {
  try {
    return new Date(iso).toLocaleDateString(lang === 'es' ? 'es-US' : 'en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso.slice(0, 10);
  }
};

const CreditLedgerCard: React.FC<Props> = ({ userId, balance, showPayments = true }) => {
  const { lang } = useI18n();
  const es = lang === 'es';
  const [rows, setRows] = useState<LedgerEntry[]>([]);
  const [pays, setPays] = useState<PaymentEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'credits' | 'payments'>('credits');

  useEffect(() => {
    let alive = true;
    const load = async () => {
      if (!userId) {
        setLoading(false);
        return;
      }
      const [l, p] = await Promise.all([fetchLedger(userId), showPayments ? fetchPayments(userId) : Promise.resolve([])]);
      if (!alive) return;
      setRows(l);
      setPays(p as PaymentEntry[]);
      setLoading(false);
    };
    void load();
    const off = onCreditsChanged(() => void load());
    return () => {
      alive = false;
      off();
    };
  }, [userId, showPayments]);

  const added = rows.filter((r) => r.delta > 0).reduce((s, r) => s + r.delta, 0);
  const spent = rows.filter((r) => r.delta < 0).reduce((s, r) => s + Math.abs(r.delta), 0);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-400">
          <History className="h-4 w-4 text-orange-500" />
          {es ? 'Historial de créditos' : 'Credit history'}
        </h3>
        {showPayments && (
          <div className="flex rounded-lg border border-slate-200 p-0.5">
            {(['credits', 'payments'] as const).map((k) => (
              <button
                key={k}
                onClick={() => setTab(k)}
                className={`rounded-md px-3 py-1.5 text-xs font-bold transition-colors ${
                  tab === k ? 'bg-slate-900 text-white' : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                {k === 'credits'
                  ? es
                    ? 'Créditos'
                    : 'Credits'
                  : es
                  ? 'Pagos'
                  : 'Payments'}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="mt-4 grid grid-cols-3 gap-3">
        {[
          { label: es ? 'Agregados' : 'Added', value: `+${added}`, tone: 'text-emerald-600' },
          { label: es ? 'Gastados' : 'Spent', value: `-${spent}`, tone: 'text-slate-700' },
          { label: es ? 'Saldo' : 'Balance', value: String(balance), tone: 'text-slate-900' },
        ].map((s) => (
          <div key={s.label} className="rounded-xl bg-slate-50 p-3 text-center">
            <p className={`text-2xl font-extrabold tabular-nums ${s.tone}`}>{s.value}</p>
            <p className="mt-0.5 text-[11px] font-bold uppercase tracking-wide text-slate-400">{s.label}</p>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="mt-6 flex items-center justify-center gap-2 py-6 text-sm text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin" /> {es ? 'Cargando…' : 'Loading…'}
        </div>
      ) : tab === 'credits' ? (
        rows.length === 0 ? (
          <p className="mt-6 rounded-xl bg-slate-50 p-4 text-center text-sm text-slate-500">
            {es
              ? 'Todavía no hay movimientos de créditos.'
              : 'No credit movements yet.'}
          </p>
        ) : (
          <ul className="mt-5 divide-y divide-slate-100">
            {rows.map((r) => {
              const positive = r.delta > 0;
              return (
                <li key={r.id} className="flex items-start justify-between gap-3 py-3">
                  <div className="min-w-0">
                    <p className="flex items-center gap-1.5 text-sm font-bold text-slate-800">
                      {positive ? (
                        <ArrowUpRight className="h-4 w-4 shrink-0 text-emerald-600" />
                      ) : (
                        <ArrowDownRight className="h-4 w-4 shrink-0 text-slate-400" />
                      )}
                      {ledgerLabel(r.kind, lang)}
                    </p>
                    {r.note && <p className="mt-0.5 truncate text-xs text-slate-500">{r.note}</p>}
                    <p className="mt-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                      {dateLabel(r.created_at, lang)}
                      {r.plan_code ? ` · ${r.plan_code}` : ''}
                    </p>
                  </div>
                  <span
                    className={`shrink-0 text-base font-extrabold tabular-nums ${
                      positive ? 'text-emerald-600' : 'text-slate-500'
                    }`}
                  >
                    {positive ? '+' : ''}
                    {r.delta}
                  </span>
                </li>
              );
            })}
          </ul>
        )
      ) : pays.length === 0 ? (
        <p className="mt-6 rounded-xl bg-slate-50 p-4 text-center text-sm text-slate-500">
          {es ? 'Todavía no hay pagos.' : 'No payments yet.'}
        </p>
      ) : (
        <ul className="mt-5 divide-y divide-slate-100">
          {pays.map((p) => (
            <li key={p.id} className="flex items-start justify-between gap-3 py-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-bold text-slate-800">
                  {p.description || p.kind}
                </p>
                <p className="mt-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  {dateLabel(p.created_at, lang)} · {p.status}
                </p>
              </div>
              <span className="shrink-0 text-base font-extrabold tabular-nums text-slate-900">
                {money(p.amount)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CreditLedgerCard;
